<script setup lang="ts">
/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Use framework.
 */
const { find, findChildren } = useFramework()

/**
 * Use query params.
 */
const { config } = useParams()

/**
 * Check the device type.
 */
const { isDesktop } = useDeviceType()

/**
 * Root level.
 */
const root = {
    id: undefined,
    name: 'Overview',
    hasChildren: true,
    parent: undefined
} as unknown as Dimension

/**
 * Current dimension.
 */
const current = computed(() => {
    return find(config.dimension)
})

/**
 * Get the list of dimensions.
 */
const list = computed(() => {
    const list: Dimension[] = []
    let dimension = unref(current)

    while (dimension) {
        list.unshift(dimension)
        dimension = find(dimension.parent)
    }

    list.unshift(root)

    return list
})

/**
 * List of options for dropdown.
 */
const options = computed(() => {
    return unref(list)
        .concat(findChildren(unref(current)?.id) ?? [])
        .map(item => {
            let padding = 0
            let parent = item.parent

            while (parent) {
                padding += 15
                parent = find(parent)?.parent
            }

            return {
                value: item.id,
                label: item.name,
                padding
            }
        })
})

/**
 * Whether to display the select options button.
 */
const showSelect = computed(() => {
    return !unref(current) || unref(current)?.hasChildren
})
</script>
    
<template>
    <div class="flex items-center gap-1 py-2 lg:pb-0">
        <template v-if="isDesktop">
            <WorldBreadcrumbList :items="list" />

            <WorldBreadcrumbItem
                v-if="showSelect"
                :key="current?.id"
                :dimension="current"
                show-children
                class="flex-shrink-0"
                data-tutorial-step="1"
                :data-tutorial-text="t('tutorial-breadcrumb')"
            >
                <template #text>
                    <PlusIcon class="h-4 w-4 text-deep-blue lg:hidden" />
                    <span class="hidden truncate lg:block">
                        {{ t('select-options') }}
                    </span>
                </template>
            </WorldBreadcrumbItem>
        </template>

        <template v-else>
            <UiSelect
                v-model="config.dimension"
                class="max-w-full flex-1 text-xs"
                :options="options"
                group-label="groupLabel"
            />
        </template>
    </div>
</template>